import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../i18n/formatters';
import { categoryLabel } from './categoryLabel';

const TYPE_COLORS = {
  curriculum: '#1AB0F2',
  sel: '#ec4899',
  safety: '#FF5151',
  health: '#26C774',
  motivational: '#7F4BF6',
  classroom: '#7F4BF6',
  seasonal: '#f97316',
  library: '#6366f1',
  stem: '#06b6d4',
  arts: '#d946ef',
  pe: '#14b8a6',
};

function getIndent(depth) {
  return 8 + depth * 3;
}

export default function CategoryTree({ fetchRootCategories, fetchCategoryChildren, selectedCategoryId, onCategorySelect, onViewAll, totalPosters, filters }) {
  const { t } = useTranslation('printables');
  // Scalar key so the effects below can depend on the filter without churning
  // on object identity; the ref keeps async callbacks reading the fresh value.
  // `ui` is part of the key: a language switch must refetch so the worker can
  // answer with displayNames in the new language (state document titles and
  // course / strand headings under Curriculum › States — 2026-09-08).
  const filterKey = `${filters?.language ?? ''}|${filters?.jurisdiction ?? ''}|${filters?.curriculum ?? ''}|${filters?.ui ?? ''}`;
  const filterRef = useRef(filters);
  filterRef.current = filters;
  const [roots, setRoots] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => {
    try {
      const saved = sessionStorage.getItem('printables_expanded_ids');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [childrenMap, setChildrenMap] = useState({});
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const restoredRef = useRef(false);

  // Region / language / curriculum changed → every cached count in this tree is
  // now wrong. Drop the fetched data and let the two effects below rebuild it.
  // The expanded set survives: category ids are filter-independent, only the
  // counts move.
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setLoaded(false);
    setRoots([]);
    setChildrenMap({});
    setLoadingIds(new Set());
    restoredRef.current = false;
  }, [filterKey]);

  useEffect(() => {
    if (loaded) return;
    fetchRootCategories(filterRef.current || {}).then(data => {
      setRoots(data.categories || []);
      setLoaded(true);
    }).catch(() => {});
  }, [fetchRootCategories, loaded, filterKey]);

  // Persist expanded IDs to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('printables_expanded_ids', JSON.stringify([...expandedIds]));
  }, [expandedIds]);

  // Restore children for previously expanded nodes after roots load
  useEffect(() => {
    if (!loaded || restoredRef.current || expandedIds.size === 0) return;
    restoredRef.current = true;

    expandedIds.forEach(id => {
      fetchCategoryChildren(id, filterRef.current || {})
        .then(data => {
          setChildrenMap(prev => ({ ...prev, [id]: data.children || [] }));
        })
        .catch(() => {});
    });
  }, [loaded, expandedIds, fetchCategoryChildren, filterKey]);

  const toggleExpand = useCallback(async (categoryId) => {
    const next = new Set(expandedIds);
    if (next.has(categoryId)) {
      next.delete(categoryId);
      setExpandedIds(next);
      return;
    }
    next.add(categoryId);
    setExpandedIds(next);

    if (!childrenMap[categoryId]) {
      setLoadingIds(prev => new Set(prev).add(categoryId));
      try {
        const data = await fetchCategoryChildren(categoryId, filterRef.current || {});
        setChildrenMap(prev => ({ ...prev, [categoryId]: data.children || [] }));
      } catch {}
      setLoadingIds(prev => { const s = new Set(prev); s.delete(categoryId); return s; });
    }
  }, [expandedIds, childrenMap, fetchCategoryChildren]);

  const handleSelect = useCallback((categoryId) => {
    onCategorySelect(categoryId);
  }, [onCategorySelect]);

  const renderNode = (node, depth = 0) => {
    const isExpanded = expandedIds.has(node.id);
    const isLoading = loadingIds.has(node.id);
    const isSelected = selectedCategoryId === node.id;
    const children = childrenMap[node.id] || [];
    const hasChildren = node.hasChildren !== false && (node.posterCount > 0 || children.length > 0 || depth < 3);
    const color = TYPE_COLORS[node.type] || '#8A8FA8';
    const indent = getIndent(depth);
    const isRoot = depth === 0;

    return (
      <div key={node.id}>
        <div
          onClick={() => { handleSelect(node.id); if (hasChildren) toggleExpand(node.id); }}
          title={(node.displayName || categoryLabel(t, node.name))}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 10px', paddingLeft: indent,
            cursor: 'pointer', borderRadius: 6,
            background: isSelected ? 'rgba(26, 176, 242, 0.15)' : 'transparent',
            borderLeft: depth > 0 ? `2px solid ${isSelected ? color : 'rgba(27,37,80,0.057)'}` : 'none',
            marginLeft: depth > 0 ? indent - 6 : 0,
            paddingLeft: depth > 0 ? 8 : indent,
            transition: 'all 0.15s ease',
            minHeight: 40, borderRadius: 12,
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(27,37,80,0.038)'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
        >
          {/* Chevron */}
          {hasChildren ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
              <path d="M9 6l6 6-6 6" stroke="rgba(27,37,80,0.332)" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          ) : <div style={{ width: 12, flexShrink: 0 }} />}

          {/* Color dot — only on first 2 levels to save space */}
          {depth <= 1 && (
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
          )}

          {/* Name */}
          <span style={{
            flex: 1,
            fontSize: isRoot ? '1rem' : '0.94rem',
            fontWeight: isSelected ? 600 : isRoot ? 500 : 400,
            color: isSelected ? '#0A6A99' : isRoot ? 'rgba(27,37,80,0.76)' : 'rgba(27,37,80,0.57)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}>
            {(node.displayName || categoryLabel(t, node.name))}
          </span>

          {/* Poster count */}
          {node.posterCount > 0 && (
            <span style={{
              fontSize: '0.8rem', fontWeight: 800, color: 'rgba(27,37,80,0.6)',
              background: '#FFFFFF', padding: '1px 5px', borderRadius: 8,
              fontWeight: 500, flexShrink: 0, lineHeight: 1.4,
            }}>
              {node.posterCount}
            </span>
          )}
        </div>

        {/* Children */}
        {isExpanded && (
          <div>
            {isLoading && (
              <div style={{ paddingLeft: getIndent(depth + 1) + 12, padding: '4px 8px', fontSize: '0.7rem', color: 'rgba(27,37,80,0.237)' }}>
                {t('tree.loading')}
              </div>
            )}
            {children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="bv-pr-label" style={{ padding: '0 4px' }}>
        {t('tree.categories')}
      </div>

      {/* All Printables */}
      <div
        onClick={() => onViewAll ? onViewAll() : onCategorySelect(null)}
        title={t('tree.allPrintables')}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 8px', cursor: 'pointer', borderRadius: 6,
          background: selectedCategoryId === null ? 'rgba(26, 176, 242, 0.15)' : 'transparent',
          transition: 'background 0.15s ease',
          minHeight: 40, borderRadius: 12,
        }}
        onMouseEnter={e => { if (selectedCategoryId !== null) e.currentTarget.style.background = 'rgba(27,37,80,0.038)'; }}
        onMouseLeave={e => { if (selectedCategoryId !== null) e.currentTarget.style.background = 'transparent'; }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" stroke={selectedCategoryId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" stroke={selectedCategoryId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" stroke={selectedCategoryId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" stroke={selectedCategoryId === null ? '#1AB0F2' : 'rgba(27,37,80,0.332)'} strokeWidth="2" />
        </svg>
        <span style={{ fontSize: '1rem', fontWeight: selectedCategoryId === null ? 900 : 700, color: selectedCategoryId === null ? '#0A6A99' : 'rgba(27,37,80,0.76)' }}>
          {t('tree.allPrintables')}
        </span>
        {totalPosters > 0 && (
          <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'rgba(27,37,80,0.6)', background: '#EEF0F6', padding: '2px 8px', borderRadius: 8, fontWeight: 500, marginLeft: 'auto', lineHeight: 1.4 }}>
            {formatNumber(totalPosters)}
          </span>
        )}
      </div>

      <div style={{ marginTop: 2 }}>
        {roots.map(root => renderNode(root, 0))}
      </div>
    </div>
  );
}
